/**
 * HorseCard — carte d'un cheval de « Mes Chevaux » (photo, infos, actions)
 *
 * Extraite de HorseList (cap 300 lignes) au sprint S3. Les actions rares
 * (archiver / désarchiver, supprimer) sont regroupées dans le menu « Plus
 * d'actions » (G4) ; les autres boutons de la carte sont inchangés, sauf
 * « Upload », masqué sur un cheval archivé (exclu de l'import CSV).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText, BarChart3, Upload, Pencil, ClipboardList, Trash2, Archive, ArchiveRestore, MoreHorizontal,
} from 'lucide-react';
import { getTheme, BTN, TYPE, RADIUS } from '../constants/theme';
import { semanticColors, semanticColorsDark } from '../constants/semanticColors';
import { buildHorseSubtitle } from '../utils/horseSubtitle';
import ProtectedImage from '../shared/ProtectedImage';
import HorseIcon from '../shared/HorseIcon';
import ActionsMenu from '../shared/ActionsMenu';
import LiveStatusButton, { LiveStatusLegend } from './LiveStatusButton';
import { isArchived } from './horseArchiveService';

const PHOTO_SIZE = 72;
const ACTION_BUTTON = { width: '100%', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' };

const ArchivedBadge = ({ darkMode, label }) => {
  const c = darkMode ? semanticColorsDark : semanticColors;
  return (
    <span style={{
      ...TYPE.badge, flexShrink: 0, padding: '2px 8px', borderRadius: RADIUS.pill,
      background: c.neutralBg, color: c.neutralText, border: `1px solid ${c.neutralText}`,
    }}>
      {label}
    </span>
  );
};

const HorseCard = ({
  horse, darkMode, onViewData, onUploadCSV, onEdit, onDelete, onHealth, onViewDetail, onLive,
  onArchive, onUnarchive,
}) => {
  const { t: tr } = useTranslation(); // i18n aliasé (collision avec le thème `t`)
  const t = getTheme(darkMode);
  const archived = isArchived(horse);
  const subtitle = buildHorseSubtitle(horse);

  const photoStyle = {
    width: PHOTO_SIZE, height: PHOTO_SIZE, borderRadius: '50%', border: `2px solid ${t.border}`, flexShrink: 0,
  };
  const avatarStyle = {
    ...photoStyle,
    background: darkMode ? t.surface : semanticColors.blueBg,
    display: horse.photo1 ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px',
  };

  const menuItems = [
    archived
      ? { key: 'unarchive', label: tr('horses.actions.unarchive'), Icon: ArchiveRestore, onSelect: () => onUnarchive(horse) }
      : { key: 'archive', label: tr('horses.actions.archive'), Icon: Archive, onSelect: () => onArchive(horse) },
    { key: 'delete', label: tr('common.delete'), Icon: Trash2, danger: true, onSelect: () => onDelete && onDelete(horse._id) },
  ];

  return (
    <div style={{ background: t.surface, padding: '16px', borderRadius: '8px', border: `1px solid ${t.border}`, marginBottom: '12px' }}>
      {/* Ligne 1 : Photo + Infos */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        {horse.photo1 ? (
          <ProtectedImage src={`/uploads/horses/${horse.photo1}`} thumb alt={horse.horseName} style={{ ...photoStyle, objectFit: 'cover' }} />
        ) : null}
        <div style={avatarStyle}>
          <HorseIcon size={40} color={t.accent} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', minWidth: 0 }}>
            <h3 style={{
              color: t.textPrimary, margin: 0, fontSize: '17px', fontWeight: '600',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {horse.horseName}
            </h3>
            {archived && <ArchivedBadge darkMode={darkMode} label={tr('horses.list.archivedBadge')} />}
          </div>
          {subtitle && (
            <p style={{ color: t.textSecondary, fontSize: '13px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {subtitle}
            </p>
          )}
          {horse.equipmentId && <LiveStatusLegend live={horse.live} darkMode={darkMode} />}
        </div>
      </div>

      {/* Ligne 2 : Boutons — grille 2×2 sur mobile */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
        <button onClick={() => onViewDetail && onViewDetail(horse._id)} style={{ ...BTN.primary, ...ACTION_BUTTON }}>
          <FileText size={15} /> {tr('horses.actions.viewDetail')}
        </button>
        <button onClick={() => onViewData(horse._id)} style={{ ...BTN.secondary(darkMode), ...ACTION_BUTTON }}>
          <BarChart3 size={15} /> {tr('horses.actions.data')}
        </button>
        {/* Masqué sur un archivé : l'import CSV n'accepte pas les chevaux archivés */}
        {!archived && (
          <button onClick={() => onUploadCSV(horse._id)} style={{ ...BTN.secondary(darkMode), ...ACTION_BUTTON }}>
            <Upload size={15} /> {tr('horses.actions.upload')}
          </button>
        )}
        <button onClick={() => onEdit && onEdit(horse._id)} style={{ ...BTN.secondary(darkMode), ...ACTION_BUTTON }}>
          <Pencil size={15} /> {tr('common.edit')}
        </button>
        <button onClick={() => onHealth && onHealth(horse._id)} style={{ ...BTN.secondary(darkMode), ...ACTION_BUTTON }}>
          <ClipboardList size={15} /> {tr('horses.actions.health')}
        </button>
        {horse.equipmentId && (
          <LiveStatusButton live={horse.live} darkMode={darkMode} onClick={() => onLive && onLive(horse._id)} />
        )}
        <ActionsMenu
          label={tr('horses.actions.more')}
          Icon={MoreHorizontal}
          items={menuItems}
          darkMode={darkMode}
          buttonStyle={{ ...BTN.secondary(darkMode), ...ACTION_BUTTON }}
        />
      </div>
    </div>
  );
};

export default HorseCard;
